import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedBtn = dropdownBtnRef.current && dropdownBtnRef.current.contains(target);
      const clickedInside = dropdownRef.current && dropdownRef.current.contains(target);
      if (!clickedBtn && !clickedInside) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20product%20photography%20collage%20with%20pastel%20sneakers%20hoodies%20caps%20and%20fashion%20accessories%20arranged%20on%20light%20cream%20surface%20with%20gentle%20natural%20lighting%20clean%20european%20aesthetic%20airy%20composition%20editorial%20style%20high%20detail%20soft%20shadows%20harmonious%20warm%20neutral%20palette&width=1600&height=900&seq=hero-bg-gtbuy&orientation=landscape"
          alt="GTBuy product collection background"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>

      <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="inline-block border border-white/60 text-white text-xs md:text-sm px-4 py-1.5 rounded-full mb-5 md:mb-6 tracking-wide">
            Product Comparison Engine
          </span>

          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white font-light leading-tight tracking-tight">
            Find the Best Deals
            <br />
            <span className="font-semibold">Using GTBuy Spreadsheet</span>
            <br />
            <span className="text-white/80">Intelligence</span>
          </h1>

          <p className="mt-4 md:mt-5 text-white/70 text-sm md:text-base max-w-lg leading-relaxed">
            Compare 5000+ products across Kakobuy and Superbuy with structured spreadsheet data. Stop guessing &mdash; start comparing intelligently.
          </p>

          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3" ref={dropdownRef}>
            <div className="relative">
              <button
                ref={dropdownBtnRef}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full sm:w-auto flex items-center justify-between gap-2 bg-white text-foreground-900 px-5 py-3 rounded-md text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-base"></i>
                <span>Search Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-1 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-64 overflow-y-auto z-20">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              gtbuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="border border-white/70 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              buy it now
              <i className="ri-shopping-bag-line"></i>
            </a>
          </div>

          <div className="mt-5 md:mt-6 flex items-center gap-5 text-white/50 text-xs">
            <span>5000+ products indexed</span>
            <span className="text-white/30">|</span>
            <span>100+ comparison sheets</span>
            <span className="text-white/30">|</span>
            <span>Updated daily</span>
          </div>
        </div>
      </div>
    </section>
  );
}